import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { Installment } from '../types/installment';
import { Observable } from 'rxjs';
import { Decimal } from '../../core/utils/decimal';
import { FIRST_ENFORCEMENT_CASES_FORFEIT_CREDIT_ID } from '../types/credit';
import { Payment } from '../types/payment';
import { Accrual, InstallmentAccrualBuckets } from '../types/accrual';

@Injectable()
export class InstallmentService {
  constructor(public request: RequestService) { }

  getInstallmentList(creditId: number | string): Observable<Installment[]> {
    return this.request.get(['credits', creditId, 'installments']);
  }

  getInstallmentById(
    creditId: number | string,
    installmentId: number | string
  ): Observable<Installment> {
    return this.request.get([
      'credits',
      creditId,
      'installments',
      installmentId,
    ]);
  }

  updateInstallmentForfeitEnabled(creditId: number | string, installmentId: number | string, forfeitEnabled: boolean) {
    return this.request.post(['credits', creditId, 'installments', installmentId, 'forfeit-enabled'], {
      body: forfeitEnabled,
    });
  }

  getInstallmentPayments(installment: Installment, paymentList: Payment[]) {
    return paymentList.filter(
      (payment: Payment) =>
        payment.installment && payment.installment.id === installment.id
    );
  }

  getPaymentTotalPerComponent(paymentList: Payment[]) {
    return {
      principal: paymentList
        .reduce(
          (acc, current) => acc.plus(new Decimal(current.principal)),
          new Decimal(0)
        )
        .toNumber(),

      forfeit: paymentList
        .reduce(
          (acc, current) => acc.plus(new Decimal(current.forfeit)),
          new Decimal(0)
        )
        .toNumber(),

      interest: paymentList
        .reduce(
          (acc, current) => acc.plus(new Decimal(current.interest)),
          new Decimal(0)
        )
        .toNumber(),

      penalty: paymentList
        .reduce(
          (acc, current) => acc.plus(new Decimal(current.penalty)),
          new Decimal(0)
        )
        .toNumber(),

      utilizationFee: paymentList
        .reduce(
          (acc, current) => acc.plus(new Decimal(current.utilizationFee)),
          new Decimal(0)
        )
        .toNumber(),
    };
  }

  getInstallmentAccruals(
    installment: Installment,
    accruals
  ): Partial<InstallmentAccrualBuckets> {
    const installmentAccruals: Partial<InstallmentAccrualBuckets> = {
      forfeit: [],
      interest: [],
      penalty: [],
      utilizationFee: [],
    };

    if (accruals) {
      Object.keys(installmentAccruals).forEach((key) => {
        if (accruals[key]) {
          installmentAccruals[key] = accruals[key].filter(
            (accrual) =>
              accrual.installment && accrual.installment.id === installment.id
          );
        }
      });
    }

    return installmentAccruals;
  }

  hasBeenCorrectedByOperator(accruals) {
    return accruals.some(
      (accrual: Accrual) =>
        accrual.amount &&
        accrual.createdBy &&
        accrual.createdBy?.email !== 'system@itfgroupbg.com'
    );
  }

  getReceivables(installment: Installment, payments: Payment[], accruals: Accrual[]) {
    const installmentPayments = this.getInstallmentPayments(installment, payments);
    const totalPayments = this.getPaymentTotalPerComponent(installmentPayments);
    const installmentAccruals = this.getInstallmentAccruals(installment, accruals);

    // Helper: check if any accrual for a given component was adjusted by an operator.
    const hasBeenDeducted = (component: string): boolean => {
      if (!accruals) {
        return false;
      }
      return this.hasBeenCorrectedByOperator(installmentAccruals[component] || []);
    };
    return [
      {
        component: 'principal',
        installment: installment.principal,
        receivables: installment.principal,
        paidTotal: totalPayments.principal,
        hasBeenDeducted: false,
      },
      {
        component: 'interest',
        installment: installment.interest,
        receivables: installment.accruedInterest,
        paidTotal: totalPayments.interest,
        hasBeenDeducted: hasBeenDeducted('interest'),
      },
      {
        component: 'forfeit',
        // TODO: remove after non ENFORCEMENT_CASES are not active
        installment:
          installment.credit.id >= FIRST_ENFORCEMENT_CASES_FORFEIT_CREDIT_ID
            ? installment.accruedForfeit
            : installment.forfeit,
        receivables: installment.accruedForfeit,
        paidTotal: totalPayments.forfeit,
        hasBeenDeducted: hasBeenDeducted('forfeit'),
      },
      {
        component: 'utilizationFee',
        installment: installment.utilizationFee,
        receivables: installment.accruedUtilizationFee,
        paidTotal: totalPayments.utilizationFee,
        hasBeenDeducted: hasBeenDeducted('utilizationFee'),
      },
      {
        component: 'overduePenalty', // TODO: rename to penalty
        // Overdue penalty does not have a scheduled installment value.
        installment: 0,
        receivables: installment.accruedPenalty,
        paidTotal: totalPayments.penalty,
        hasBeenDeducted: hasBeenDeducted('penalty'),
      },
      {
        component: 'total',
        // TODO: remove after non ENFORCEMENT_CASES are not active
        installment:
          installment.credit.id >= FIRST_ENFORCEMENT_CASES_FORFEIT_CREDIT_ID
            ? new Decimal(installment.total)
              .minus(installment.forfeit)
              .add(installment.accruedForfeit)
              .toNumber()
            : installment.total,
        receivables: Decimal.sumNumbers(
          installment.principal,
          installment.accruedInterest,
          installment.accruedForfeit,
          installment.accruedPenalty,
          installment.accruedUtilizationFee
        ),
        paidTotal: Decimal.sumNumbers(
          totalPayments.principal,
          totalPayments.interest,
          totalPayments.forfeit,
          totalPayments.penalty,
          totalPayments.utilizationFee
        ),
        hasBeenDeducted: false,
      },
    ];
  }
}
